/**
 * NOTE: This requires `@sentry/nextjs` version 7.3.0 or higher.
 *
 * NOTE: If using this with `next` version 12.2.0 or lower, uncomment the
 * penultimate line in `CustomErrorComponent`.
 *
 * This page is loaded by Nextjs:
 *  - on the server, when data-fetching methods throw or reject
 *  - on the client, when `getInitialProps` throws or rejects
 *  - on the client, when a React lifecycle method throws or rejects, and it's
 *    caught by the built-in Nextjs error boundary
 *
 * See:
 *  - https://nextjs.org/docs/basic-features/data-fetching/overview
 *  - https://nextjs.org/docs/api-reference/data-fetching/get-initial-props
 *  - https://reactjs.org/docs/error-boundaries.html
 */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/nextjs';

import { resetUserProfile } from '../store/modules/profile/actions';

import getCookie from '../utils/getCookie';
import deleteCookies from '../utils/deleteCookies';
import { Page, Content } from '../theme/page';
import Typography from '../atoms/typography';
import Button from '../atoms/button';
import TaxonShop from '../tissues/category-taxon-shop';

const PageContent = styled(Content)`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const Image = styled.img`
  margin-bottom: ${(props) => props.theme.modularScale.base};
  width: 120px;
`;

const Title = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  margin-bottom: ${(props) => props.theme.modularScale.xlarge};
`;

const Body = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  margin-bottom: ${(props) => props.theme.modularScale.large};
  max-width: 380px;
  text-align: center;
  width: 100%;
`;

const Cta = styled(Button)`
  margin-bottom: ${(props) => props.theme.modularScale['3xlarge']};
`;

const taxonData = {
  one_line: true,
  heading: 'Shop by Category',
  as_taxon: false,
  elements: [
    {
      path: '/shop/baby',
      icon: {
        url: '/images/404/maisonette-baby.png',
        width: 130,
        height: 130,
        alt: 'Shop baby!',
        mime: 'image/png'
      },
      heading: 'Baby'
    },
    {
      path: '/shop/kids',
      icon: {
        url: '/images/404/maisonette-kids.png',
        width: 130,
        height: 130,
        alt: 'Shop kids',
        mime: 'image/png'
      },
      heading: 'Kids'
    },
    {
      path: '/shop/play',
      icon: {
        url: '/images/404/maisonette-play.png',
        width: 130,
        height: 130,
        alt: 'Shop play',
        mime: 'image/png'
      },
      heading: 'Play'
    },
    {
      path: '/shop/home',
      icon: {
        url: '/images/404/maisonette-home.png',
        width: 130,
        height: 130,
        alt: 'Shop home',
        mime: 'image/png'
      },
      heading: 'Home'
    },
    {
      path: '/shop/gifts',
      icon: {
        url: '/images/404/maisonette-gifts.png',
        width: 130,
        height: 130,
        alt: 'Shop gifts',
        mime: 'image/png'
      },
      heading: 'Gifts'
    }
  ]
};

const ErrorPage = (props) => {
  useEffect(() => {
    if (props.statusCode === 500) {
      const ignoreOrderToken = !getCookie('maisonette_user_token');
      deleteCookies(ignoreOrderToken);
      props.resetUserProfile();
    }
  }, [props]);

  // If you're using a Nextjs version prior to 12.2.1, uncomment this to
  // compensate for https://github.com/vercel/next.js/issues/8592
  Sentry.captureUnderscoreErrorException(props);

  return (
    <Page background="cream" id="maincontent">
      <PageContent layout="large">
        <figure>
          <Image src="/images/error.png" alt="Error" />
        </figure>
        <Title element="h3" like="heading-4">
          {props.statusCode === 404 ? 'Page Not Found!' : 'Oops!'}
        </Title>
        <Body element="p" like="paragraph-2">
          Seems you’ve fallen down the rabbit hole. Make like Alice and find your way back by
          choosing a category below. No eating little cakes required.
        </Body>
        <Cta outline isLink href="/">Go to homepage</Cta>
      </PageContent>
      <Content>
        <TaxonShop data={taxonData} uppercaseTitles paddingsOnMobile />
      </Content>
    </Page>
  );
};

ErrorPage.getInitialProps = async (contextData) => {
  // In case this is running in a serverless function, await this in order to give Sentry
  // time to send the error before the lambda exits
  await Sentry.captureUnderscoreErrorException(contextData);

  // Set a default status code in case a status code is not returned on the response
  let statusCode = 404;

  // These will be undefined if the page is not found
  const { res, err } = contextData;
  // Use the statusCode from the response if available, otherwise check the error
  statusCode = res ? res.statusCode : statusCode;
  statusCode = err ? err.statusCode : statusCode;

  // Return the status code to the page props as expected by the ErrorPage component
  return { statusCode };
};

ErrorPage.propTypes = {
  resetUserProfile: PropTypes.func.isRequired,
  statusCode: PropTypes.number.isRequired
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  resetUserProfile: () => dispatch(resetUserProfile())
});

export default connect(mapStateToProps, mapDispatchToProps)(ErrorPage);
